import React, {useCallback, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import lodashIsEqual from 'lodash/isEqual';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import ListSubheader from '@material-ui/core/ListSubheader';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Tooltip from '@material-ui/core/Tooltip';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {LabelInput} from '@components/label-input/LabelInput';
import {Property} from 'csstype';

// Used for aria controls
const menuId = 'menu-list-grow';

export interface MenuGrowProps<
  Value,
  Option extends {title: string; value: Value; group?: string}
> {
  value: Value;
  onChange: (v: Value) => void;
  options: Array<Option>;

  // Alternate rendering for each option's line item
  renderValue?: (o: Option) => JSX.Element;
  // Render the component rotated 90deg
  vertical?: boolean;
  // placeholder for when nothing is selected
  placeholder?: string;
  // equality check, defaults to _.isEqual
  isEqual?: (a: any, b: any) => boolean;
  // affects Mui component sizes
  size?: 'small' | 'medium' | 'large';
  // Names to render for each group
  groupLabels?: Record<string, string>;
  // Background colors for each group
  groupColors?: Record<string, Property.Color>;
}

/**
 * Generic animated dropdown
 *
 * Supports grouping elements via the option props: groupLabels and groupColors
 * When grouping elements, make sure each option has a group property
 */
export const MenuGrow = <Value, Option extends {title: string; value: Value; group?: string}>({
  value,
  onChange,
  renderValue = option => <span>{option.title}</span>,
  options,
  placeholder = 'Select...',
  vertical,
  size = 'medium',
  groupLabels,
  groupColors,
  isEqual = lodashIsEqual,
}: MenuGrowProps<Value, Option>) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);

  const selectedOption = useMemo(() => {
    const option = options.find(opt => isEqual(opt.value, value));
    return option ? renderValue(option) : 'N/A';
  }, [isEqual, options, renderValue, value]);

  const searchedOptions = useMemo(
    () =>
      search
        ? options.filter(opt => opt.title?.toLowerCase().includes(search.toLowerCase()))
        : options,
    [options, search]
  );

  const renderItem = useCallback(
    (opt: Option) => (
      <Tooltip key={opt.group + opt.title} title={opt.title?.length > 37 ? opt.title : ''}>
        <MenuItem
          onClick={() => {
            onChange(opt.value);
            setSearch('');
            setOpen(false);
          }}>
          {opt.title}
        </MenuItem>
      </Tooltip>
    ),
    [onChange, setOpen]
  );

  const css = useStyles();
  return (
    <div className={css.root}>
      <div>
        <Button
          ref={anchorRef}
          size={size}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          onClick={() => setOpen(s => !s)}
          className={cn(css.button, {
            [css.placeholderStyle]: !selectedOption,
          })}
          classes={{
            endIcon: cn(css.endIcon, {
              [css.endIconMedium]: size === 'medium',
            }),
          }}>
          <div className={vertical ? css.verticalText : css.horizontalText}>
            {selectedOption || placeholder}
            <Icon
              style={vertical ? {marginTop: 10} : {marginLeft: 10}}
              className={`material-icons-round`}>
              east
            </Icon>
          </div>
        </Button>
        <Popper
          className={css.popper}
          open={open}
          anchorEl={anchorRef.current}
          style={{width: anchorRef?.current?.offsetWidth}}
          role={undefined}
          transition
          disablePortal>
          {({TransitionProps, placement}) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}>
              <div>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                  <Paper>
                    <div className={css.inputWithClose}>
                      <LabelInput
                        name="filter-menu-grow-items"
                        autoFocus
                        labelPlacement="bottom"
                        value={search}
                        onChange={evt => setSearch(evt.target.value)}
                        placeholder="Search"
                      />
                      <IconButton size="small" onClick={() => setOpen(false)}>
                        <CloseIcon />
                      </IconButton>
                    </div>
                    <MenuList className={css.menuList} id={menuId}>
                      {groupLabels
                        ? Object.entries(groupLabels).map(([key, label]) => {
                            const groupItems = searchedOptions.filter(el => el?.group === key);
                            const color = groupColors?.[key];
                            if (!groupItems.length) return null;

                            return (
                              <li
                                style={color ? {backgroundColor: color} : {}}
                                key={`option-${key}`}
                                className={css.listSection}>
                                <ul className={css.ulStyle}>
                                  <ListSubheader>{label}</ListSubheader>
                                  {groupItems.map(renderItem)}
                                </ul>
                              </li>
                            );
                          })
                        : searchedOptions.map(renderItem)}
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              </div>
            </Grow>
          )}
        </Popper>
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  button: {
    ...theme.typography.body1,
    justifyContent: 'space-between',
    minWidth: 'unset',
  },
  verticalText: {
    writingMode: 'vertical-lr',
    transform: 'rotate(180deg)',
    padding: '5px 0px',
    display: 'flex',
    alignItems: 'center',
    maxHeight: 500,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.primary.dark,
  },
  horizontalText: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 550,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.primary.dark,
  },
  icon: {
    color: theme.palette.grey[500],
    transition: theme.transitions.create(['transform']),
  },
  iconOpen: {
    transform: 'rotate(180deg)',
  },
  iconVertical: {
    transform: 'rotate(90deg)',
  },
  inputWithClose: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexFlow: 'row nowrap',
    paddingTop: 10,
    paddingRight: 12,
  },
  endIcon: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    height: 23,
    paddingLeft: 2,
    '& > span': {
      marginTop: 2,
    },
  },
  endIconMedium: {
    paddingLeft: 4,
  },
  menuList: {
    backgroundColor: '#ffffff',
    maxHeight: 240,
    minHeight: 240,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 0,
    marginTop: 10,
  },
  root: {
    position: 'relative',
  },
  paper: {
    marginRight: theme.spacing(2),
  },
  placeholderStyle: {
    color: theme.palette.grey[500],
  },
  popper: {
    zIndex: 999,
    minWidth: 300,
  },
  listSection: {
    backgroundColor: 'inherit',
  },
  ulStyle: {
    backgroundColor: 'inherit',
    padding: 0,
  },
}));
